import * as React from "react";
import usePanolens from "../../hooks/usePanolens";
import Panolens from "../../panolens/panolens";
import getCssClasses from "../../utils/getCssClasses";
import "./panoramaViewStyles.scss";

export type THotspot = {
  coordinates: number[];
  title: string;
  animated?: boolean;
  delta?: number;
  image?: {
    hotspotImage: string;
    hover: boolean;
  };
  hoverElement?: HTMLElement;
};

type TInfohotstop = {
  infospot: any;
  hotspot: THotspot;
};

type TPanoramaViewProps = {
  img: string | HTMLImageElement;
  width?: string;
  height?: string;
  hotspots?: THotspot[];
  canInit?: boolean;
  customClass?: string;
  cameraZoom?: number;
  changeCameraZoomOnResize?: boolean;
  onInfospotClick?: (hotspot: TInfohotstop) => void;
  onInfospotHoverStart?: (hotspot: TInfohotstop) => void;
  onInfospotHoverEnd?: (hotspot: TInfohotstop) => void;
  onLoaded?: (panolens: Panolens) => void;
  onChange?: (viewer: any) => void;
};

let timerId;

const PanoramaView: React.FC<TPanoramaViewProps> = (props) => {
  const {
    img,
    customClass,
    hotspots,
    width = "100%",
    height = "535px",
    canInit = true,
    cameraZoom,
    changeCameraZoomOnResize = true,
    onLoaded,
    onChange,
    onInfospotClick,
    onInfospotHoverStart,
    onInfospotHoverEnd,
  } = props;
  const [loading, setLoading] = React.useState<boolean>(true);
  const divRef = React.createRef<HTMLDivElement>();

  const panolens = usePanolens(img, { onChangeHandler: onChange });

  const handleResize = () => {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => {
      panolens.setZoom();
      clearTimeout(timerId);
    }, 500);
  };

  React.useEffect(() => {
    panolens.subscribe("image-load", () => setLoading(false));
    panolens.subscribe("infospot-click", (hotspot: TInfohotstop) => {
      if (typeof onInfospotClick === "function") {
        onInfospotClick(hotspot);
      }
      console.log(hotspot);
    });
    panolens.subscribe("infospot-hoverStart", (hotspot: TInfohotstop) => {
      if (typeof onInfospotHoverStart === "function") {
        onInfospotHoverStart(hotspot);
      }
    });
    panolens.subscribe("infospot-hoverEnd", (hotspot: TInfohotstop) => {
      if (typeof onInfospotHoverEnd === "function") {
        onInfospotHoverEnd(hotspot);
      }
    });
    if (changeCameraZoomOnResize) {
      window.addEventListener("resize", handleResize);
    }

    return () => {
      panolens.unsubscribeAll();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  React.useEffect(() => {
    if (!panolens.infospots) {
      panolens.initInfospots(hotspots);
      return;
    }
    panolens.updateInfoSpots(hotspots);
  }, [hotspots]);

  React.useEffect(() => {
    if (panolens.viewer) {
      setLoading(true);
      panolens.updatePanorama(img, hotspots);
      return;
    }
  }, [img]);

  React.useEffect(() => {
    if (!loading && typeof onLoaded === "function") {
      onLoaded(panolens);
    }
  }, [loading]);

  React.useEffect(() => {
    if (!divRef.current || !canInit || panolens.viewer) {
      return;
    }

    canInit && panolens.initView(divRef.current, cameraZoom);
  }, [canInit]);

  const cssClasses = getCssClasses({
    isLoaded: !loading,
    [customClass || ""]: !!customClass,
  });

  return (
    <div
      className="perx-panorama-view-wrapper"
      style={{ width: width || "100%", height: height }}
    >
      {(canInit || panolens.viewer) && (
        <div
          ref={divRef}
          className={`perx-panorama-view perx-panorama-view__preloader ${cssClasses}`}
          style={{ width: "100%", height: "100%" }}
        ></div>
      )}
    </div>
  );
};

export default PanoramaView;
